import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { StepTitle, NavButtons } from "@/components/onboard/ui";
import { CreditCard, LockKey, Info } from "@phosphor-icons/react";
import { RazorpayCardButton } from "@/components/RazorpayCheckout";

export function StepPayment({ token, state, onSaved, onBack }) {
  const [consent, setConsent] = useState(false);
  const [local, setLocal] = useState(null);
  const s = local || state;
  const card = (s.wizard.payment || {}).card;
  const saved = card && card.provider === "razorpay";

  if (!state.razorpay_enabled) return <PayByLink token={token} onSaved={onSaved} onBack={onBack} />;
  return (
    <div>
      <StepTitle kicker="Step 9 · Auto Pay" title="Auto Pay Authorization" sub="Your dispatch fees are charged automatically every Friday. Only a $1.00 verification is made today and it's refunded." />
      <div className="rounded-2xl border border-sky-400/20 bg-sky-400/[0.05] px-5 py-4 mb-6 flex gap-3 text-sm text-sky-100" data-testid="ob-emandate-note">
        <Info size={20} className="shrink-0 text-sky-300" />
        <span>Every <b>Friday</b> we charge the total of your unpaid dispatch invoices to your saved <b>card</b> in US dollars. If a charge fails, you'll get a secure payment link by email.</span>
      </div>
      <div className={`rounded-2xl border p-5 sm:p-6 ${saved ? "border-emerald-400/40 bg-emerald-400/[0.05]" : "border-sky-400/40 bg-white/[0.03]"}`} data-testid="ob-emandate-card">
        <p className="font-heading font-bold text-white text-lg flex items-center gap-2"><CreditCard size={20} />Save your card</p>
        <p className="text-xs text-white/50 mt-0.5">Visa, Mastercard, Amex · secured by Razorpay</p>
        {saved && <p className="mt-3 text-sm text-emerald-100" data-testid="ob-card-saved">{(card.brand || "Card").toUpperCase()} ending in {card.last4} · saved for auto pay</p>}
        <label className="flex items-start gap-2.5 text-xs text-white/60 my-4">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-emerald-400" data-testid="ob-card-consent" />
          I authorize Asterism Services LLC to charge my saved card (through Razorpay) every Friday for the total of all unpaid dispatch service invoices, or when I click Pay. This authorization stays in effect until I turn off auto pay in the Carrier App.
        </label>
        <RazorpayCardButton setupUrl={`/onboard/${token}/card/setup`} confirmUrl={`/onboard/${token}/card/confirm`} consent={consent} onSaved={setLocal} testid="ob-card-open"
          className="h-11 px-6 rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 text-white text-sm font-semibold hover:brightness-110 disabled:opacity-40 transition-[filter,opacity]">{saved ? "Replace Card" : "Save Card Securely"}</RazorpayCardButton>
      </div>
      <p className="mt-5 text-xs text-white/45 flex items-center gap-1.5"><LockKey size={14} />Card details are handled by Razorpay. Asterism never sees or stores your full card number or CVV.</p>
      <NavButtons onBack={onBack} onNext={() => onSaved(s)} disabled={!saved} nextLabel="Continue to Agreement" testid="ob-emandate-continue" />
    </div>
  );
}

function PayByLink({ token, onSaved, onBack }) {
  const [loading, setLoading] = useState(false);
  const next = async () => {
    setLoading(true);
    try { const { data } = await api.post(`/onboard/${token}/save`, { step: "payment", data: {} }); onSaved(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <div>
      <StepTitle kicker="Step 9 · Payments" title="How You'll Pay" sub="Card auto pay is being switched on. Until then, you'll pay each dispatch invoice through a secure link." />
      <div className="rounded-2xl border border-sky-400/20 bg-sky-400/[0.05] px-5 py-4 flex gap-3 text-sm text-sky-100" data-testid="ob-emandate-unavailable">
        <Info size={20} className="shrink-0 text-sky-300" />
        <span>We'll email you a secure payment link (US dollars) for every dispatch invoice. You can turn on card auto pay later in the Carrier App.</span>
      </div>
      <NavButtons onBack={onBack} onNext={next} loading={loading} nextLabel="Continue to Agreement" testid="ob-paylink-continue" />
    </div>
  );
}
